import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import { ONCOUNTRY_STEPS, ONCOUNTRY_TAKEAWAYS } from "@/data/academy";
import { Check } from "lucide-react";

const TRUCK = "https://customer-assets.emergentagent.com/job_academy-2/artifacts/2zesbrjw_seedkeepers%20truck%20with%20logo.png";

export default function OnCountry() {
  return (
    <div data-testid="oncountry-page">
      <PageHero
        kicker="On-Country learning"
        title="Hands-on native seed training, delivered where the work happens."
        subtitle="A practical combination of field work, classroom-style sessions and seed processing that prepares participants to identify, collect, clean, store and plan native seed work."
        image={TRUCK}
      >
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/contact" data-testid="oncountry-discuss" className="bg-cream text-rainforest px-8 py-4 text-sm tracking-wide hover:bg-sandstone transition-colors">Discuss an Intensive</Link>
          <Link to="/contact" className="border border-cream/50 text-cream px-8 py-4 text-sm tracking-wide hover:bg-cream hover:text-rainforest transition-colors">Request a Program Outline</Link>
        </div>
      </PageHero>

      <section className="bg-cream py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal className="max-w-3xl mb-14">
            <p className="overline text-ochre mb-6">The pathway</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal leading-tight">Every participant develops a specific project.</h2>
            <p className="mt-6 text-charcoal/70 text-lg font-light">Programs usually run three to five days and are adapted to local Country, species, season and organisational priorities. Each participant leaves with something they built.</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
            {[
              "A local seed collection calendar",
              "A species field guide",
              "A seed handling protocol",
              "A seed bank plan",
              "A propagation trial",
              "A restoration seed strategy",
              "A regional priority species list",
            ].map((p, i) => (
              <Reveal key={p} delay={(i % 2) * 0.05}>
                <div className="flex items-baseline gap-5 py-5 border-b border-charcoal/12">
                  <span className="font-serif text-2xl text-ochre">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-2xl md:text-3xl text-rainforest">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14">
            <p className="font-serif text-3xl md:text-4xl font-light text-eucalyptus leading-snug max-w-3xl">
              Building Australia's native seed knowledge base — one participant, one project at a time.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-eucalyptus text-cream py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            <p className="overline text-ochre mb-6">What participants take away</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight">Practical skills for the field.</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {ONCOUNTRY_TAKEAWAYS.map((t, i) => (
              <Reveal key={t} delay={(i % 2) * 0.05}>
                <div className="flex items-start gap-3 py-3 border-b border-cream/15">
                  <Check size={16} className="text-ochre mt-1 shrink-0" />
                  <span className="text-cream/85 font-light">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-24 md:py-32" data-testid="oncountry-enquiry">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <Reveal className="mb-10 text-center">
            <p className="overline text-ochre mb-4">Discuss an intensive</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal">Bring the Academy to your Country.</h2>
          </Reveal>
          <Reveal><EnquiryForm formType="on-country" title="Request a program outline" cta="Discuss an Intensive" /></Reveal>
        </div>
      </section>
    </div>
  );
}
